import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Divider,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "i18n-fe";
import { TableSkeleton, RideStatusBadge } from "common-fe";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { useRideStatuses } from "../hooks/useRideStatuses";
import { useLoadedFor } from "../hooks/useLoadedFor";
import { formatPlacing } from "../util/placingDisplay";
import RideLink from "./RideLink";
import ClassLink from "./ClassLink";

// Shared read-only ride-history drawer for PeopleManager (#507, riderId) and
// HorseManager (#507, horseId) — a person/horse's basic info plus every Ride
// they appear on (score-api RidesApi.listRides(classId, showId, day, ring,
// needsReview, riderId, horseId): exactly one of riderId/horseId is used
// here — mind the positional slots, #637 inserted showId/day/ring). Each ride row
// links into the #508 ride editor route; that route doesn't exist yet, so the
// link may 404 until #508 ships — it's still wired per the card.
//
// `filterKey` is "riderId" or "horseId"; `subjectId` is the person/horse id.
// `infoItems` is the caller-built [{ label, value }] list of basic fields.
// `children` renders between the info block and the ride history.
export default function RideHistoryDrawer({
  open,
  onClose,
  title,
  infoItems = [],
  filterKey,
  subjectId,
  children = null,
}) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();

  const [rides, setRides] = useState([]);
  const [classesById, setClassesById] = useState({});
  // #3186 -- derived from "loaded for THIS subject", so it is true on the very
  // first draw after the drawer opens (or the subject changes) and a previous
  // subject's rides are never shown while the new ones load.
  const loadKey = open && subjectId ? `${filterKey}:${subjectId}` : null;
  const [loading, markLoaded] = useLoadedFor(loadKey);
  const [error, setError] = useState(null);

  useEffect(() => {
    markLoaded(null);
    if (!open || !subjectId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setError(null);
        const list = unwrap(
          // listRides positional signature (after #637 added showId/day/ring):
          // (classId, showId, day, ring, needsReview, riderId, horseId).
          await scoreApi.rides.listRides(
            undefined, // classId
            undefined, // showId
            undefined, // day
            undefined, // ring
            undefined, // needsReview
            filterKey === "riderId" ? subjectId : undefined,
            filterKey === "horseId" ? subjectId : undefined
          )
        );
        if (cancelled) return;
        setRides(list);

        // Best-effort class resolution (classId -> full class) so the table
        // shows classNumberLabel() via ClassLink (#864) instead of raw ids;
        // falls back to null (ClassLink then links the bare id) on failure.
        const uniqueClassIds = [...new Set(list.map((r) => r.classId).filter(Boolean))];
        const entries = await Promise.all(
          uniqueClassIds.map(async (classId) => {
            try {
              return [classId, unwrap(await scoreApi.classes.getClass(classId))];
            } catch {
              return [classId, null];
            }
          })
        );
        if (!cancelled) setClassesById(Object.fromEntries(entries));
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) markLoaded(`${filterKey}:${subjectId}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, subjectId, filterKey, scoreApi, markLoaded]);

  const rows = useMemo(() => rides, [rides]);

  // Single server-derived badge status per ride (#544/#546) instead of the raw
  // stored ride.status string.
  const rideIds = useMemo(() => rows.map((r) => r.rideId || r.id).filter(Boolean), [rows]);
  const { statuses: rideStatuses, termReasons: rideTermReasons = {} } = useRideStatuses(rideIds);

  return (
    // AppLayout's AppBar (common-fe) is pinned at zIndex 1300 — the same as
    // MUI's default Modal z-index Drawer uses — so without bumping this it
    // paints over the drawer's title/close button. Bump above it.
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.modal + 100 }}
    >
      <Box sx={{ width: { xs: "100vw", sm: 480 }, p: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h5" color="text.primary">
            {title}
          </Typography>
          <IconButton onClick={onClose} aria-label={t("rideHistory.close")}>
            <CloseIcon />
          </IconButton>
        </Box>

        {infoItems.length > 0 && (
          <Box sx={{ mb: 3 }}>
            {infoItems.map((item) => (
              <Typography key={item.label} variant="body2" color="text.secondary">
                <strong>{item.label}:</strong> {item.value || "-"}
              </Typography>
            ))}
          </Box>
        )}

        <Divider sx={{ mb: 2 }} />

        {/* Card #3610 -- a caller-supplied section above the ride history (the
            People page puts the rider's Scoresheet recipients editor here). */}
        {children}

        <Typography variant="h6" gutterBottom color="text.primary">
          {t("rideHistory.title")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message || t("rideHistory.loadError")}
          </Alert>
        )}

        {loading ? (
          <TableSkeleton rows={3} columns={4} dense />
        ) : rows.length === 0 ? (
          <Typography color="text.secondary">{t("rideHistory.empty")}</Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("rideHistory.columns.class")}</TableCell>
                  <TableCell>{t("rideHistory.columns.ride")}</TableCell>
                  <TableCell>{t("rideHistory.columns.status")}</TableCell>
                  <TableCell>{t("rideHistory.columns.placing")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((ride) => {
                  const rideId = ride.rideId || ride.id;
                  const classObj = classesById[ride.classId];
                  // #865 — a missing class name is a data problem to surface
                  // quietly (placeholder), not a raw id to print in the cell.
                  // The id still reaches support via the tooltip, and — when
                  // present — ClassLink still links through to /classes/:id.
                  const classLookupMissed = !!ride.classId && !classObj;
                  return (
                    <TableRow key={rideId} hover>
                      <TableCell>
                        {/* #864 — shared ClassLink; classesById[ride.classId] is
                            the resolved class object when the best-effort lookup
                            above succeeded, else null (link falls back to the
                            placeholder below). */}
                        <Tooltip title={classLookupMissed ? ride.classId : ""}>
                          <span>
                            <ClassLink
                              classId={ride.classId}
                              classObj={classObj}
                              fallback={t("common.unknownClass")}
                            />
                          </span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {/* #860 — shared RideLink ("#CNO, Rider on Horse"),
                            linked into the RideEditor route. Supersedes #550's
                            class-name-only link — a rider's history can span
                            multiple horses, so CNO + rider + horse matters
                            here more than most sites. */}
                        <RideLink ride={ride} rideId={rideId} />
                      </TableCell>
                      <TableCell>
                        <RideStatusBadge status={rideStatuses[rideId]} termReason={rideTermReasons[rideId]} />
                      </TableCell>
                      <TableCell data-testid="ride-history-placing-cell">{formatPlacing(t, ride.placing)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Drawer>
  );
}
